<template>
	<div class="inline-video-player">
		<div class="inline-video-player__thumbnail">
			<PictureImg
				v-if="thumbnailPoster"
				v-show="!isPlaying && thumbnailPoster"
				:media="thumbnailPoster"
				class="inline-video-player__poster"
			/>
			<div v-if="isLoading" class="spinner"></div>
			<video
				v-if="src"
				ref="videoElement"
				class="inline-video-player__video"
				type="video/mp4"
				:poster="thumbnailPoster?.src"
				:id="uniqueId"
				:muted="isVideoMuted"
				preload="none"
				@playing="hideSpinnerWhenPlaying"
				@waiting="showSpinnerWhenWaiting"
				@ended="detectVideoEnded"
				@pause="detectVideoPaused"
				playsinline
			>
				<source :data-src="src" type="video/mp4" />
			</video>
			<button
				v-if="src"
				type="button"
				:class="[
					'video-control-btn',
					{
						'btn-hidden': isPlaying,
					},
				]"
				@click="!isPlaying ? playVideo() : pauseVideo(videoElement, $event)"
				:aria-label="isPlaying ? pauseButtonAriaLabel : playButtonAriaLabel"
			>
				<div class="video-control-btn__icon-wrapper">
					<SvgIcon icon="play" v-show="!isPlaying && !isLoading" />
				</div>
			</button>
			<button
				v-if="src"
				class="mute-btn"
				type="button"
				@click="toggleSound()"
				:aria-label="isVideoMuted ? unmuteButtonAriaLabel : muteButtonAriaLabel"
			>
				<SvgIcon icon="soundon" v-show="!isVideoMuted" />
				<SvgIcon icon="soundoff" v-show="isVideoMuted" />
			</button>
		</div>
	</div>
</template>

<script setup>
import PictureImg from "./PictureImg.vue";

const { pushTracking, trackVideo } = useTracking();

const props = defineProps({
	src: { type: String, required: true, default: null },
	thumbnailPoster: { type: Object, required: false, default: null },
	muteButtonAriaLabel: {
		type: String,
		required: false,
		default: "Mute video.",
	},
	unmuteButtonAriaLabel: {
		type: String,
		required: false,
		default: "Unmute video.",
	},
	playButtonAriaLabel: {
		type: String,
		required: false,
		default: "Play video.",
	},
	pauseButtonAriaLabel: {
		type: String,
		required: false,
		default: "Pause video.",
	},
	isAutoplay: { type: Boolean, required: false, default: false },
});

const uniqueId = useId();
const observer = ref(null);
const isPlaying = ref(false);
const videoElement = ref(null);
const isVideoMuted = ref(true);
const isLoading = ref(false);
const isMediaLoaded = ref(false);

onMounted(() => {
	observer.value = new IntersectionObserver(onVideoIntersectionObserved, {
		threshold: 0.5,
	});
	observer.value.observe(videoElement.value);
});

function hideSpinnerWhenPlaying() {
	if (!videoElement.value) return;
	if (videoElement.value.readyState >= 3) isLoading.value = false;
}

function showSpinnerWhenWaiting() {
	isLoading.value = true;
}

function toggleSound() {
	isVideoMuted.value = !isVideoMuted.value;
}

function loadVideo(video) {
	if (isMediaLoaded.value || !video) return;

	video.children[0].src = video.children[0].dataset.src;
	video.load();
	isMediaLoaded.value = true;
	video.oncanplay = () => playVideo(video);

	trackVideo(video);
}

function playVideo() {
	if (!videoElement.value) return;

	if (!isMediaLoaded.value) {
		loadVideo(videoElement.value);
	}

	if (videoElement.value.id) {
		videoElement.value.play();
		pushTracking(props.tracking?.clickMedia);
	} else if (!videoElement.value.paused) pauseVideo(videoElement.value);

	isPlaying.value = true;
}

function pauseVideo() {
	if (!videoElement.value) return;

	if (detectVideoPaused()) {
		videoElement.value.pause();
		isLoading.value = false;
		isPlaying.value = false;
	}
}

function onVideoIntersectionObserved(entries) {
	entries.forEach(({ target, isIntersecting }) => {
		if (isIntersecting && props.isAutoplay) {
			if (isMediaLoaded.value) playVideo(target);
			loadVideo(target);
		} else if (!isIntersecting && !target.paused) {
			pauseVideo(target);
		}
	});
}

function detectVideoPaused() {
	isPlaying.value = false;
}

function detectVideoEnded() {
	isPlaying.value = false;
	pushTracking(props.tracking?.mediaEnded);
}
</script>

<style lang="scss">
.inline-video-player {
	width: 100%;
	height: 100%;
	position: relative;

	body.no-anim & {
		animation-play-state: paused;
	}

	.inline-video-player__thumbnail {
		height: 100%;
		overflow: hidden;
		position: relative;
		transition: all 0.5s ease;

		.video-control-btn {
			display: flex;
			justify-content: center;
			align-items: center;
			position: absolute;
			padding: 0;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			width: 100%;
			height: 100%;
			border-radius: var(--box-corner-radius, 15px);
			background-color: transparent;
			border: none;
			cursor: pointer;

			.video-control-btn__icon-wrapper {
				width: 80px;
				height: 80px;
				padding: 16px;
				background: black;
				opacity: 0.8;
				border-radius: 50%;
				border: 3px solid white;

				svg {
					transition: all 0.5s ease;

					path {
						fill: var(--color-white, #fff);
					}
				}
			}
		}

		.video-control-btn.btn-hidden {
			.video-control-btn__icon-wrapper {
				opacity: 0;

				svg {
					opacity: 0;
				}
			}
		}

		.mute-btn {
			height: 40px;
			width: 40px;
			position: absolute;
			right: var(--spacing-2, 16px);
			bottom: var(--spacing-2, 16px);
			padding: 0;
			z-index: 10;
			transition: all 0.5s ease;
			background-color: transparent;
			border: none;
			cursor: pointer;

			svg {
				width: 100%;
				height: 100%;
				transition: opacity 0.5s ease;

				path {
					fill: var(--color-white, #fff);
				}
			}
		}
	}

	.inline-video-player__poster {
		position: absolute;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.inline-video-player__video {
		display: block;
		background-color: var(--color-grey-8, #1b1b1b);
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: 16px;
	}

	.spinner {
		position: absolute;
		margin-left: auto;
		margin-right: auto;
		left: 0;
		right: 0;
		top: calc(50% - 40px);
		width: 80px;
		padding: var(--spacing-1, 8px);
		aspect-ratio: 1;
		border-radius: 50%;
		background: var(--color-white, #fff);
		-webkit-mask: conic-gradient(#0000 10%, #000),
			linear-gradient(#000 0 0) content-box;
		mask: conic-gradient(#0000 10%, #000), linear-gradient(#000 0 0) content-box;
		-webkit-mask-composite: source-out;
		mask-composite: subtract;
		animation: spin 1.5s infinite linear;
	}

	@keyframes spin {
		to {
			transform: rotate(1turn);
		}
	}
}

.inline-video-player-section {
	.default-section__container {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 320px;

		.inline-video-player {
			height: 100%;
			width: 100%;
		}
	}
}
</style>
